// Comprehensive Maine family law data based on Custody X Change and official Maine sources
const maineData = {
  maine: {
    name: 'Maine',
    overview: {
      keyPoints: [
        'Maine follows the "best interests of the child" standard with 9 specific factors outlined in Maine Revised Statutes Title 19-A',
        'Joint custody is not presumed - courts make decisions based on individual circumstances and evidence',
        'Maine uses "parental rights and responsibilities" terminology instead of traditional custody language',
        'The state requires mandatory mediation for all contested custody cases unless domestic violence is involved',
        'Maine has a strong emphasis on maintaining meaningful relationships between children and both parents',
        'Parenting plans are required in all cases involving minor children and must be detailed and specific',
        'Maine law prohibits relocation with a child more than 60 miles without court approval or written consent'
      ],
      uniqueFeatures: [
        'Maine uses unique terminology: "parental rights and responsibilities" instead of custody',
        'The state has a 60-mile relocation rule that is stricter than many other states',
        'Maine requires specific provisions for holiday schedules in all parenting plans',
        'The state allows for "shared parental rights and responsibilities" which is similar to joint custody',
        'Maine has specialized family drug courts in several counties',
        'The state recognizes grandparent rights under specific circumstances'
      ]
    },
    legalRequirements: {
      residency: 'Either the child or one parent must have lived in Maine for at least 6 months before filing, or Maine must be the child\'s home state',
      filing: 'File in the county where the child has lived for the past 6 months, or where either parent resides',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, or court order',
      documentation: 'Required documents include complaint, summons, vital records, and financial affidavits in all cases involving children',
      uniqueRules: [
        'Mandatory mediation required for all contested custody cases (exceptions for domestic violence)',
        'Guardian ad litem may be appointed to represent the child\'s interests',
        'Parent education programs are required in many counties for divorcing parents',
        'Maine requires specific language about relocation in all parenting plans'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Maine uses "primary residence" and "contact" instead of physical custody terminology',
      legalCustody: 'Called "parental rights and responsibilities" - includes decision-making authority for major life decisions',
      jointCustody: 'Referred to as "shared parental rights and responsibilities" - requires court approval and detailed parenting plan',
      visitation: 'Called "contact" - includes regular contact schedules, holiday time, and vacation periods',
      specialProvisions: [
        'Supervised contact may be ordered when child safety is a concern',
        'Virtual contact (video calls, phone calls) is recognized and can be court-ordered',
        'Extended contact during school vacations is standard',
        'Grandparent contact rights are protected under Maine law when in the child\'s best interests'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed schedule for regular contact including weekdays, weekends, and holidays',
        'Specific holiday and vacation schedules with alternating arrangements',
        'Transportation arrangements and responsibility for costs',
        'Communication methods between parents and between parent and child',
        'Decision-making authority for education, healthcare, religious upbringing, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements',
        'Relocation provisions and notification requirements',
        'Any restrictions or special conditions for either parent'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times and locations',
        'Include detailed holiday schedules to avoid future conflicts',
        'Address how to handle make-up time when contact is missed',
        'Include provisions for the child\'s participation in extracurricular activities',
        'Specify how major decisions will be made and communicated',
        'Include technology use guidelines and virtual contact provisions',
        'Address how changes to the schedule will be requested and approved'
      ]
    },
    childSupport: {
      guidelines: 'Maine uses the Income Shares Model based on both parents\' gross income and the number of children',
      factors: [
        'Gross income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Significant contact time (more than 25% overnight stays)',
        'Special needs of the children requiring additional expenses'
      ],
      calculation: 'Maine Child Support Guidelines using standardized worksheets based on combined gross income',
      modifications: 'Substantial change in circumstances required, typically 15% change in support amount or significant change in contact time',
      enforcement: 'Strong enforcement through Maine DHHS including wage garnishment, tax intercept, license suspension, and asset seizure',
      detailedInfo: {
        basicCalculation: {
          description: 'Maine follows the Income Shares Model, which estimates the amount parents would spend on their children if the family were intact.',
          steps: [
            'Calculate each parent\'s gross weekly income from all sources',
            'Determine allowable deductions (taxes, mandatory retirement, other child support)',
            'Calculate net weekly income for each parent',
            'Add both parents\' net incomes to get combined net weekly income',
            'Use the Maine Child Support Guidelines table to find basic support amount',
            'Allocate support proportionally based on each parent\'s income percentage',
            'Add costs for health insurance, childcare, and extraordinary expenses',
            'Adjust for significant contact time if applicable (more than 25% overnights)'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, and bonuses',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, and interest',
            'Rental property income (after expenses)',
            'Retirement and pension benefits',
            'Social Security benefits (including disability)',
            'Unemployment compensation and workers\' compensation',
            'Military pay including allowances',
            'Spousal support received from previous relationships',
            'Trust income and annuities'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, etc.)',
            'Foster care payments',
            'Child support received for other children',
            'Gifts and inheritances (unless regular income source)',
            'Student loans and grants',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or partner'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes',
            'State income taxes',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions',
            'Union dues (if required for employment)',
            'Court-ordered child support for other children',
            'Court-ordered spousal support to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child',
            'Childcare expenses necessary for work or education',
            'Extraordinary medical expenses for the child'
          ]
        },
        contactTimeAdjustment: {
          description: 'Maine allows adjustment to child support when the paying parent has significant contact time with the child.',
          thresholds: [
            'No adjustment for less than 91 overnights per year (25%)',
            'Potential adjustment for 91+ overnights per year (25%+)',
            'Standard adjustment formula applies when contact time is significant',
            'Court has discretion to deviate from guidelines based on best interests'
          ],
          calculation: 'Adjustment reduces support obligation proportionally based on overnight percentage and additional costs'
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically for the child',
            allocation: 'Shared proportionally based on parents\' income percentages',
            requirements: 'Must be reasonable in cost and provide adequate coverage'
          },
          childcare: {
            description: 'Necessary childcare costs for work, education, or job training',
            allocation: 'Shared proportionally based on parents\' income percentages',
            requirements: 'Must be work-related or education-related necessity'
          },
          extraordinaryMedical: {
            description: 'Uninsured medical expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance',
              'Orthodontic treatment',
              'Vision care and corrective lenses',
              'Mental health counseling and therapy',
              'Physical therapy and rehabilitation',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies',
              'Treatment for chronic conditions'
            ],
            allocation: 'Shared proportionally based on parents\' income percentages'
          },
          extracurricular: {
            description: 'Costs for children\'s activities and enrichment programs',
            examples: [
              'Sports team fees and equipment',
              'Music lessons and instrument rental',
              'Dance, martial arts, or gymnastics classes',
              'Summer camps and day programs',
              'School trips and educational activities',
              'Tutoring and educational support services'
            ],
            note: 'Not automatically included in basic support - must be agreed upon by parents or court-ordered'
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 15% or more)',
            'Change in contact time arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Emancipation or change in custody of a child'
          ],
          process: [
            'File Motion for Modification of Child Support',
            'Serve the other parent with motion and supporting documents',
            'Provide updated financial affidavits and documentation',
            'Attend mediation if required by local court rules',
            'Attend hearing before family law magistrate or judge'
          ],
          timeline: 'Modifications are effective from the date of filing the motion, not the date of the court order'
        },
        enforcement: {
          methods: [
            'Income withholding (automatic wage garnishment)',
            'Asset seizure and bank account levies',
            'Tax refund intercept (state and federal)',
            'License suspension (driver\'s, professional, recreational)',
            'Credit bureau reporting',
            'Contempt of court proceedings',
            'Property liens and asset seizure',
            'Passport denial for arrearages over $2,500',
            'Lottery winnings intercept'
          ],
          interest: 'Unpaid support accrues interest at the rate set by Maine statute',
          statute: 'No statute of limitations on collecting past-due child support'
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'Maine Judicial Branch Family Division',
          url: 'https://www.courts.maine.gov/maine_courts/family/index.shtml',
          description: 'Official Maine court system family division with forms, procedures, and self-help resources'
        },
        {
          name: 'Maine Family Law Forms',
          url: 'https://www.courts.maine.gov/maine_courts/family/forms/index.shtml',
          description: 'Complete collection of Maine family court forms for divorce, custody, and support cases'
        },
        {
          name: 'Maine Court Self-Help Centers',
          url: 'https://www.courts.maine.gov/maine_courts/help/index.shtml',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'Cumberland County Family Court',
          url: 'https://www.courts.maine.gov/courts/family/cumberland.html',
          phone: '(207) 822-4200',
          description: 'Family court serving Portland area and Cumberland County'
        },
        {
          name: 'Penobscot County Family Court',
          url: 'https://www.courts.maine.gov/courts/family/penobscot.html',
          phone: '(207) 561-2300',
          description: 'Family court serving Bangor area and Penobscot County'
        },
        {
          name: 'York County Family Court',
          url: 'https://www.courts.maine.gov/courts/family/york.html',
          phone: '(207) 459-2100',
          description: 'Family court serving southern Maine and York County'
        }
      ],
      support: [
        {
          name: 'Maine DHHS Child Support Services',
          url: 'https://www.maine.gov/dhhs/ofi/services-programs/child-support',
          phone: '(207) 287-2886',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Maine Child Support Payment Center',
          url: 'https://www.maine.gov/dhhs/ofi/services-programs/child-support/make-payment',
          description: 'Official payment processing and case management for Maine child support'
        },
        {
          name: 'Pine Tree Legal Assistance',
          url: 'https://www.ptla.org/',
          phone: '(207) 774-8211',
          description: 'Free legal services for low-income families in family law matters'
        },
        {
          name: 'Maine Volunteer Lawyers Project',
          url: 'https://www.vlp.org/',
          phone: '(207) 774-4753',
          description: 'Pro bono legal services and clinics for family law cases'
        },
        {
          name: 'Maine Coalition to End Domestic Violence',
          url: 'https://www.mcedv.org/',
          phone: '1-866-834-4357',
          description: 'Statewide domestic violence resources and legal advocacy'
        },
        {
          name: 'Fathers Rights Maine',
          url: 'https://www.facebook.com/groups/fathersrightsmaine',
          description: 'Support group and advocacy for fathers navigating family court in Maine'
        }
      ],
      legal: [
        {
          name: 'Maine State Bar Association Lawyer Referral Service',
          url: 'https://www.mainebar.org/page/LawyerReferralService',
          phone: '(207) 623-1121',
          description: 'Professional attorney referrals for family law cases throughout Maine'
        },
        {
          name: 'Maine Family Law Specialists',
          url: 'https://www.mainebar.org/page/FamilyLaw',
          description: 'Directory of attorneys specializing in family law and certified by Maine Bar'
        },
        {
          name: 'University of Maine School of Law Legal Clinic',
          url: 'https://mainelaw.maine.edu/academics/clinical-programs/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Maine Collaborative Divorce Professionals',
          url: 'https://www.collaborativedivorce.net/maine',
          description: 'Network of collaborative divorce attorneys and professionals in Maine'
        },
        {
          name: 'Maine Mediation Services',
          url: 'https://www.courts.maine.gov/maine_courts/adr/index.shtml',
          description: 'Court-connected and private mediation services for family disputes'
        }
      ],
      educational: [
        {
          name: 'Children First Program',
          url: 'https://www.courts.maine.gov/maine_courts/family/children_first/index.shtml',
          description: 'Mandatory parent education program for divorcing parents with minor children'
        },
        {
          name: 'Maine Parenting Plan Resources',
          url: 'https://www.courts.maine.gov/maine_courts/family/parenting_plans/index.shtml',
          description: 'Comprehensive guide to creating effective parenting plans in Maine'
        },
        {
          name: 'Maine Guardian ad Litem Program',
          url: 'https://www.courts.maine.gov/maine_courts/family/gal/index.shtml',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'Co-Parenting Resources Maine',
          url: 'https://extension.umaine.edu/family-relationships/',
          description: 'University of Maine extension programs for co-parenting education and support'
        },
        {
          name: 'Maine Parent Information and Resource Centers',
          url: 'https://www.maine.gov/doe/Learning/specialservices/parentinfo',
          description: 'Educational resources for parents navigating special needs and educational decisions'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'Cumberland County Family Court',
            address: '205 Newbury Street, Portland, ME 04101',
            phone: '(207) 822-4200',
            jurisdiction: 'Portland, South Portland, Westbrook, Gorham, and all of Cumberland County'
          },
          {
            name: 'Penobscot County Family Court',
            address: '73 Harlow Street, Bangor, ME 04401',
            phone: '(207) 561-2300',
            jurisdiction: 'Bangor, Orrington, Old Town, and all of Penobscot County'
          },
          {
            name: 'York County Family Court',
            address: '25 Adams Street, Biddeford, ME 04005',
            phone: '(207) 459-2100',
            jurisdiction: 'Biddeford, Saco, Sanford, Kennebunk, and all of York County'
          },
          {
            name: 'Androscoggin County Family Court',
            address: '71 Lisbon Street, Lewiston, ME 04240',
            phone: '(207) 795-4800',
            jurisdiction: 'Lewiston, Auburn, and all of Androscoggin County'
          },
          {
            name: 'Kennebec County Family Court',
            address: '95 State Street, Augusta, ME 04330',
            phone: '(207) 622-7200',
            jurisdiction: 'Augusta, Waterville, Gardiner, and all of Kennebec County'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$120.00',
          'Divorce (without children)': '$120.00',
          'Custody/Parental Rights': '$120.00',
          'Child Support': '$120.00',
          'Modification': '$120.00',
          'Paternity': '$120.00',
          'Protection from Abuse': 'No fee'
        },
        requiredForms: [
          'Complaint for Divorce/Parental Rights',
          'Summons',
          'Vital Statistics Form',
          'Financial Statement (if children or support involved)',
          'Parenting Plan (if children involved)',
          'Child Support Guidelines Worksheet',
          'Certificate of Service'
        ]
      },
      statutes: [
        {
          name: 'Maine Revised Statutes Title 19-A (Domestic Relations)',
          url: 'https://legislature.maine.gov/legis/statutes/19-A/title19-Ach61sec0.html',
          description: 'Primary statutes governing divorce, custody, and family law in Maine'
        },
        {
          name: 'Maine Child Support Guidelines (19-A M.R.S. § 2006)',
          url: 'https://legislature.maine.gov/legis/statutes/19-A/title19-Asec2006.html',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'Maine Parental Rights and Responsibilities Act (19-A M.R.S. § 1501 et seq.)',
          url: 'https://legislature.maine.gov/legis/statutes/19-A/title19-Ach53sec0.html',
          description: 'Comprehensive custody and parenting time laws for Maine'
        },
        {
          name: 'Maine Uniform Child Custody Jurisdiction Act (19-A M.R.S. § 1731 et seq.)',
          url: 'https://legislature.maine.gov/legis/statutes/19-A/title19-Ach55sec0.html',
          description: 'Interstate custody jurisdiction and enforcement laws'
        }
      ],
      emergencyContacts: [
        {
          name: 'Maine Domestic Violence Hotline',
          phone: '1-866-834-4357',
          description: '24/7 statewide domestic violence crisis line'
        },
        {
          name: 'Maine Child Abuse Hotline',
          phone: '1-800-452-1999',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'Crisis & Counseling Centers',
          phone: '211',
          description: 'Dial 211 for mental health crisis intervention and counseling referrals'
        },
        {
          name: 'Maine Legal Aid Hotline',
          phone: '1-800-442-4293',
          description: 'Legal assistance hotline for low-income individuals'
        }
      ]
    },
    bestInterestsFactors: [
      'The child\'s age and the child\'s relationship with each parent',
      'Each parent\'s capacity and disposition to give the child love, affection, and guidance',
      'Each parent\'s capacity to continue the education and rearing of the child',
      'Each parent\'s capacity to cooperate in matters concerning the child',
      'The length of time the child has lived in a stable, satisfactory environment',
      'The stability of each parent\'s home environment',
      'The moral and emotional fitness of each parent',
      'Each parent\'s history of domestic violence, if any',
      'The child\'s preference, if the child is of sufficient age and maturity'
    ],
    practicalTips: [
      'Maine courts strongly favor detailed, specific parenting plans over general arrangements',
      'Document all interactions and communications with the other parent',
      'Attend all required parent education classes - failure to complete can delay your case',
      'Be prepared for mandatory mediation - come with realistic proposals and flexibility',
      'Keep detailed records of your time with your children and expenses',
      'Follow all court orders precisely - violations can seriously harm your case',
      'Consider hiring a local attorney familiar with Maine family court judges and procedures'
    ]
  }
};

export default maineData;